// =============================================
// Product Catalog Types
// =============================================

export type ProductType = 'single' | 'bundle' | 'kit';

export type MediaType = 'image' | 'video' | 'label' | 'nutrition_image';

export type SectionType =
  | 'bullets'
  | 'long_description'
  | 'usage'
  | 'unique_ingredients'
  | 'ingredients_list'
  | 'warnings';

export type DocumentType = 'certification' | 'datasheet';

export type RelationType = 'frequently_bought_together' | 'recommended';

// =============================================
// Base Interfaces
// =============================================

export interface Product {
  id: string;
  source: string;
  slug: string;
  name: string;
  brand: string | null;
  sku: string;
  product_type: ProductType;
  primary_category: string | null;
  category_path: string | null;
  short_tagline: string | null;
  unit_value: number | null;
  unit_name: string | null;
  price_gross: number | null;
  currency: string;
  base_price_value: number | null;
  base_price_unit: string | null;
  rating_avg: number | null;
  rating_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductMedia {
  id: string;
  product_id: string;
  type: MediaType;
  url: string;
  alt: string | null;
  sort_order: number;
  created_at: string;
}

export interface ProductSection {
  id: string;
  product_id: string;
  section_type: SectionType;
  title: string;
  content: string;
  sort_order: number;
  created_at: string;
}

export interface ProductAttribute {
  id: string;
  product_id: string;
  key: string;
  value: string;
  group_name: string;
  sort_order: number;
  created_at: string;
}

export interface BundleItem {
  id: string;
  bundle_product_id: string;
  child_product_id: string | null;
  child_product_name: string;
  quantity_text: string | null;
  sort_order: number;
  created_at: string;
  // Joined data
  child_product?: Product;
}

export interface ProductDocument {
  id: string;
  product_id: string;
  type: DocumentType;
  title: string;
  url: string;
  sort_order: number;
  created_at: string;
}

export interface ProductRelation {
  id: string;
  product_id: string;
  related_product_id: string | null;
  related_external_url: string | null;
  relation_type: RelationType;
  sort_order: number;
  created_at: string;
  // Joined data
  related_product?: Product;
}

// =============================================
// Extended / Joined Interfaces
// =============================================

export interface ProductWithMedia extends Product {
  media: ProductMedia[];
}

export interface ProductFull extends Product {
  media: ProductMedia[];
  sections: ProductSection[];
  attributes: ProductAttribute[];
  documents: ProductDocument[];
  bundle_items: BundleItem[];
  relations: ProductRelation[];
}

// =============================================
// Labels & Display Helpers
// =============================================

export const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  single: 'Einzelprodukt',
  bundle: 'Bundle',
  kit: 'Set',
};

export const SECTION_TYPE_LABELS: Record<SectionType, string> = {
  bullets: 'Highlights',
  long_description: 'Beschreibung',
  usage: 'Anwendung',
  unique_ingredients: 'Besondere Inhaltsstoffe',
  ingredients_list: 'Zutaten',
  warnings: 'Hinweise',
};

export const SECTION_TYPE_ORDER: SectionType[] = [
  'bullets',
  'long_description',
  'usage',
  'unique_ingredients',
  'ingredients_list',
  'warnings',
];

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  certification: 'Zertifikat',
  datasheet: 'Datenblatt',
};

export const RELATION_TYPE_LABELS: Record<RelationType, string> = {
  frequently_bought_together: 'Wird oft zusammen gekauft',
  recommended: 'Empfohlen',
};

// =============================================
// User Product Prices
// =============================================

export type UserPriceType = 'custom' | 'discount_10' | 'discount_20' | 'discount_30';

export interface UserProductPrice {
  id: string;
  user_id: string;
  product_id: string;
  price_type: UserPriceType;
  custom_price: number | null;
  created_at: string;
  updated_at: string;
}

export interface CreateUserProductPriceInput {
  product_id: string;
  price_type: UserPriceType;
  custom_price?: number | null;
}

export interface UpdateUserProductPriceInput {
  price_type?: UserPriceType;
  custom_price?: number | null;
}

export const USER_PRICE_TYPE_LABELS: Record<UserPriceType, string> = {
  custom: 'Eigener Preis',
  discount_10: '-10%',
  discount_20: '-20%',
  discount_30: '-30%',
};

export function calculateUserPrice(onlinePrice: number, priceType: UserPriceType, customPrice: number | null): number {
  switch (priceType) {
    case 'discount_10':
      return onlinePrice * 0.9;
    case 'discount_20':
      return onlinePrice * 0.8;
    case 'discount_30':
      return onlinePrice * 0.7;
    case 'custom':
      return customPrice ?? onlinePrice;
    default:
      return onlinePrice;
  }
}

// =============================================
// Product Selection for Site Blocks
// =============================================

// Selected product in PRODUCT_SHOWCASE block - allows user customization
export interface SelectedProduct {
  productId: string;
  customName?: string;        // Override the default product name
  customDescription?: string; // Override the default tagline
  customBadge?: string;       // Custom badge like "Bestseller", "Neu", etc.
}

// Product grouped by category for the selector UI
export interface ProductsByCategory {
  category: string;
  products: Product[];
}
